/* eslint-disable no-unused-vars */
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {
    useEffect,
    useMemo, useState
} from "react";
import { Button, Form, Icon, Label, Modal } from "semantic-ui-react";
import { HotelAddonAvailability } from "../../api/services/next";
import { ChainAddonsDropDown } from '../../components/shared/ChainAddonsDropDown';
import { DatePicker } from '../../components/shared/DatePicker';
import IsAvailableForSaleDropDown from '../../components/shared/IsAvailableForSaleDropDown';
import { onError, onSuccess, onWarning } from "../../components/shared/messages";
import NumericInput from '../../components/shared/NumericInput';
import { useLoader } from '../../hooks/useLoader';
import WeekDaysSelect from '../../components/shared/WeekDaysSelect';
import { useTextResources } from '../../language';

const EditHotelAddonAvailabilityPopup = (props) => {
    const text = useTextResources();
    const loader = useLoader();

    const [isSaving, setIsSaving] = useState(false);
    const [dates, setDates] = useState([
        props.fromDate ? moment(props.fromDate).toDate() : new Date(),
        props.toDate ? moment(props.toDate).toDate() : new Date()
    ]);
    const [days, setDays] = useState(null);
    const [selectedDates, setSelectedDates] = useState([]);
    const [selectedChainAddonIds, setSelectedChainAddonIds] = useState(props.selectedChainAddonIds || []);
    const [isAvailableForSale, setIsAvailableForSale] = useState();
    const [quantity, setQuantity] = useState();
    
    useEffect(() => {
        setSelectedChainAddonIds(props.selectedChainAddonIds)
    }, [props.selectedChainAddonIds])

    const chainAddonsSpec = useMemo(() => ({
        HotelId: props.hotelId,
        InventoryManagedForHotel: true
    }), []);
    
    const isSaveEnabled = useMemo(() => {
        return (
            !isSaving &&
            selectedDates?.length > 0 &&
            selectedChainAddonIds?.length > 0 &&
            (quantity || !_.isNil(isAvailableForSale))
        );
    }, [
        isSaving,
        selectedDates,
        selectedChainAddonIds,
        quantity,
        isAvailableForSale
    ]);

    const memoDates = useMemo(() => {
        return selectedDates.length
            ? selectedDates
            : [{ startDate: dates[0], endDate: dates[1] }];
    }, [dates, selectedDates]);


    const handleDatesChanged = (newValue) => {
        if (newValue) {
            setDates(newValue);
        } else {
            setDates([new Date(), new Date()]);
        }
    };

    const addDateToList = () => {
        if (
            selectedDates.some(
                (d) =>
                    moment(d.startDate).isSame(dates[0], 'day') &&
                    moment(d.endDate).isSame(dates[1], 'day')
            )
        ) {
            onWarning(text('WarningDuplicateDates'));
            return;
        }
        setSelectedDates((selectedDates) =>
            selectedDates.concat({
                startDate: dates[0],
                endDate: dates[1],
            })
        );
    };

    const onRemoveDateFromSelectedDates = (index, e) => {
        if (e) {
            setSelectedDates((selectedDates) =>
                selectedDates.filter((_, i) => i !== index)
            );
        }
    };

    const closeAction = (refetch = false) => {
        setSelectedDates([])
        setSelectedChainAddonIds([])
        setIsAvailableForSale()
        setQuantity()
        setDays()
        props.onClose(refetch)
    }

    const saveAction = async () => {
        try {
            setIsSaving(true);
            loader(true);

            var spec = {
                HotelId: props.hotelId,
                ChainAddonIds: selectedChainAddonIds,
                Quantity: quantity,
                IsAvailableForSale: isAvailableForSale,
                DayOfWeeks: days,
                DateRangeList: selectedDates
            }

            await HotelAddonAvailability.UpdateAvailability(spec)

            closeAction(true)

            onSuccess();
        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error);
        } finally {
            loader(false);
            setIsSaving(false);
        }
    };

    return (
        <Modal size="small" open={props.open}>
            <Modal.Header
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                {text("UpdateInventory")}
            </Modal.Header>
            <Modal.Content >
                <Form>
                    <Form.Group unstackable widths={'equal'} style={{ flexWrap: 'wrap' }}>
                        {selectedDates.map((selectedDate, index) => (
                            <Label.Group key={index}>
                                <Label as='a'>
                                    {moment(selectedDate.startDate).format('DD/MM/YYYY')}-
                                    {moment(selectedDate.endDate).format('DD/MM/YYYY')}
                                    <Icon
                                        key={index}
                                        name={'delete'}
                                        onClick={onRemoveDateFromSelectedDates.bind(this, index)}
                                    />
                                </Label>
                            </Label.Group>
                        ))}
                    </Form.Group>
                    <Form.Group unstackable style={{ alignItems: 'flex-end' }}>
                        <Form.Field width={12} style={{ display: 'grid' }}>
                            <DatePicker
                                clearable={false}
                                picktype='range'
                                onChange={handleDatesChanged}
                                label={text('DateRange')}
                                value={dates}
                                minDate={moment().startOf('day').toDate()}
                            />
                        </Form.Field>
                        <Form.Field widths={4}>
                            <Button size='small' primary onClick={addDateToList}>
                                {text('AddDate')}
                            </Button>
                        </Form.Field>
                    </Form.Group>
                    <Form.Field
                        label={text('Weekdays')}
                        control={WeekDaysSelect}
                        onChange={setDays}
                        dates={memoDates}
                    />
                    <Form.Field
                        label={text('Addons')}
                        control={ChainAddonsDropDown}
                        spec={chainAddonsSpec}
                        onChange={setSelectedChainAddonIds}
                        value={selectedChainAddonIds}
                        multiple
                        search
                        required
                    />
                    <Form.Group>
                        <Form.Field
                            label={text('Quantity')}
                            control={NumericInput}
                            onChange={setQuantity}
                            min={0}
                            digits={0}
                            nullable
                            width={8}
                        />
                        <Form.Field
                            label={text('IsAvailableForSale')}
                            control={IsAvailableForSaleDropDown}
                            onChange={setIsAvailableForSale}
                            nullable
                            width={8}
                        />
                    </Form.Group>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button disabled={isSaving} negative size='small' onClick={closeAction}>
                    {' '}
                    {text('Close')}
                </Button>
                <Button
                    disabled={!isSaveEnabled}
                    loading={isSaving}
                    positive
                    size="small"
                    onClick={saveAction}
                >
                    {text("Save")}
                </Button>
            </Modal.Actions>
        </Modal>
    )
}

EditHotelAddonAvailabilityPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    fromDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    toDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    hotelId: PropTypes.number,
    selectedChainAddonIds: PropTypes.array
};
export default EditHotelAddonAvailabilityPopup;
